/**
 * PitchDeckModal — renders the Project Sentinel POC pitch deck inside a
 * modal dialog that can be expanded into native fullscreen.
 *
 * The deck itself is a static HTML bundle served by Vite out of
 * `ui/public/slides/Deck.html` (the design source of truth is
 * `assets/Project Sentinel/slides/Deck.html` — see the provenance
 * comment at the top of the live file). We load it via an <iframe>
 * rather than inlining so the deck's scroll-driven navigation,
 * `<deck-stage>` custom element, and per-slide animations remain
 * byte-identical to the standalone viewer.
 *
 * Fullscreen is handled via the Fullscreen API on the iframe element.
 * When `requestFullscreen` is unavailable (older browsers, embedded
 * webviews), we fall back to a CSS-only "immersive" mode that expands
 * the modal panel to the full viewport.
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby`.
 *   - Focus trap cycling between Close / Fullscreen buttons while open.
 *   - Escape closes (unless the browser already owns Escape because we
 *     are currently in native fullscreen — then Escape exits fullscreen
 *     and a subsequent press closes the modal).
 *   - Focus returns to the trigger that opened the modal on close.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface PitchDeckModalProps {
  /** Whether the modal is currently open. */
  open: boolean;
  /** Invoked when the user closes the modal (backdrop / Escape / X). */
  onClose: () => void;
  /**
   * Element that triggered the modal — focus is returned here on close.
   * Optional for tests; most callers pass a ref to the toolbar button.
   */
  returnFocusTo?: React.RefObject<HTMLElement>;
  /**
   * Override the iframe src. Defaults to the Vite-served deck path.
   * Exposed so tests can point at a fixture file.
   */
  src?: string;
}

const DEFAULT_SRC = '/slides/Deck.html';
const TITLE_ID = 'pitch-deck-modal-title';

export default function PitchDeckModal({
  open,
  onClose,
  returnFocusTo,
  src = DEFAULT_SRC,
}: PitchDeckModalProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const fsBtnRef = useRef<HTMLButtonElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  // CSS-only fallback when requestFullscreen is not available or denied.
  const [immersive, setImmersive] = useState(false);

  // Track true native fullscreen state via the Fullscreen API events.
  useEffect(() => {
    if (!open) return;
    function handleFsChange() {
      const fsEl = document.fullscreenElement;
      setIsFullscreen(fsEl !== null);
      // If the user hits the browser UI (F11 / Esc) we honour that and
      // release the CSS immersive flag too, so the state doesn't desync.
      if (fsEl === null) setImmersive((prev) => (prev ? false : prev));
    }
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, [open]);

  // Keyboard handling: Escape closes; Tab cycles inside the modal.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // If we're in native fullscreen, the browser has already begun
        // exiting and will dispatch `fullscreenchange`; let that round-trip
        // happen before closing so the user gets the "one Esc = out of FS"
        // muscle-memory behaviour.
        if (document.fullscreenElement !== null) return;
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        // Two focusable actions on the chrome (Close + Fullscreen) plus
        // the iframe. We trap focus between Close and Fullscreen so the
        // tab order never escapes into the page behind the backdrop.
        const focusables = [closeBtnRef.current, fsBtnRef.current].filter(
          (el): el is HTMLButtonElement => el !== null,
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Focus the Close button on open; return focus to the trigger on close.
  useEffect(() => {
    if (open) {
      // Wait a tick so the element is in the DOM before focusing.
      const id = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    // On close: return focus (if still in the DOM).
    const target = returnFocusTo?.current;
    if (target && typeof target.focus === 'function') {
      // Defer so we don't clash with any React re-render unmount cleanup.
      const id = window.setTimeout(() => target.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, returnFocusTo]);

  const enterFullscreen = useCallback(async () => {
    const el = iframeRef.current;
    if (!el) return;
    // Prefer the native Fullscreen API — gives the user the real
    // browser-level fullscreen (own window, Esc to exit, etc.).
    if (typeof el.requestFullscreen === 'function') {
      try {
        await el.requestFullscreen();
        return;
      } catch {
        // Permission denied / iframe sandbox forbids — fall through to
        // the CSS immersive fallback rather than leaving the user
        // stranded with a toggle that does nothing.
      }
    }
    setImmersive(true);
  }, []);

  const exitFullscreen = useCallback(async () => {
    if (document.fullscreenElement !== null && typeof document.exitFullscreen === 'function') {
      try {
        await document.exitFullscreen();
      } catch {
        // ignore — fullscreenchange will resync
      }
    }
    setImmersive(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen || immersive) void exitFullscreen();
    else void enterFullscreen();
  }, [isFullscreen, immersive, enterFullscreen, exitFullscreen]);

  if (!open) return null;

  const backdropClass = immersive
    ? 'scenarios-modal-backdrop pitch-deck-modal-backdrop pitch-deck-modal-backdrop--immersive'
    : 'scenarios-modal-backdrop pitch-deck-modal-backdrop';
  const panelClass = immersive
    ? 'pitch-deck-modal pitch-deck-modal--immersive'
    : 'pitch-deck-modal';

  const fullscreenActive = isFullscreen || immersive;
  const fsLabel = fullscreenActive ? 'Exit fullscreen' : 'Enter fullscreen';

  return (
    <div
      ref={backdropRef}
      className={backdropClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      data-testid="pitch-deck-modal"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <header className="pitch-deck-modal__head">
          <div>
            <div className="eyebrow">§ POC PARTNER BRIEFING</div>
            <h2 id={TITLE_ID} className="serif" style={{ margin: '4px 0 0', fontSize: 20 }}>
              Pitch deck
            </h2>
          </div>
          <div className="pitch-deck-modal__actions">
            <button
              ref={fsBtnRef}
              type="button"
              className="btn btn-ghost btn-sm"
              aria-label={fsLabel}
              aria-pressed={fullscreenActive}
              data-testid="pitch-deck-modal-fullscreen"
              onClick={toggleFullscreen}
              title={fsLabel}
            >
              {fullscreenActive ? (
                // Exit-fullscreen: arrows pointing in
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path
                    d="M6 2v3a1 1 0 0 1-1 1H2M10 2v3a1 1 0 0 0 1 1h3M6 14v-3a1 1 0 0 0-1-1H2M10 14v-3a1 1 0 0 1 1-1h3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                // Enter-fullscreen: arrows pointing out
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path
                    d="M2 6V3a1 1 0 0 1 1-1h3M14 6V3a1 1 0 0 0-1-1h-3M2 10v3a1 1 0 0 0 1 1h3M14 10v3a1 1 0 0 1-1 1h-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
            <button
              ref={closeBtnRef}
              type="button"
              className="btn btn-ghost btn-sm"
              aria-label="Close pitch deck"
              data-testid="pitch-deck-modal-close"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </header>
        <div className="pitch-deck-modal__body">
          <iframe
            ref={iframeRef}
            src={src}
            title="Project Sentinel pitch deck"
            // `allow="fullscreen"` + the legacy `allowFullScreen` attr cover
            // both modern Chromium + older Safari. No other permissions are
            // requested — the deck is a static bundle with no network side
            // effects.
            allow="fullscreen"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer"
            data-testid="pitch-deck-modal-iframe"
            className="pitch-deck-modal__iframe"
          />
        </div>
      </div>
    </div>
  );
}
